import { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';

// LeaseInvoicesModal — table of all invoices linked to a lease contract.
// Opens when operator clicks "Invoice" on a lease row.
//
// Features:
//   - Summary stats at top (paid / pending / overdue / outstanding amount)
//   - Filter by status
//   - Sortable table (Number, Period, Issued, Due, Amount, Status)
//   - Click any row to open the InvoiceViewModal for that invoice

export default function LeaseInvoicesModal() {
  const { modals, closeModal, openModal, rentals, rentalHistory, invoices, updateInvoice, showToast } = useApp();
  const open = modals['lease-invoices'];
  const leaseId = modals['lease-invoices-id'] || modals['lease-invoices-vid'];

  const lease = leaseId
    ? (rentals.find(r => r.id === leaseId) || rentalHistory.find(r => r.id === leaseId))
    : null;
  const linkedInvoices = useMemo(() => {
    if (!lease) return [];
    return invoices
      .filter(i => i.contract === lease.id)
      .sort((a, b) => (a.issueDate || '').localeCompare(b.issueDate || ''));
  }, [invoices, lease]);

  const [filterStatus, setFilterStatus] = useState('all');

  // Reset filter to "All" each time the modal opens or switches contract
  useEffect(() => { if (open) setFilterStatus('all'); }, [open, leaseId]);

  if (!open || !lease) return null;

  // Stats
  const paid = linkedInvoices.filter(i => i.status === 'paid');
  const pending = linkedInvoices.filter(i => i.status === 'pending' || i.status === 'issued');
  const overdue = linkedInvoices.filter(i => i.status === 'overdue');
  const drafts = linkedInvoices.filter(i => i.status === 'draft');
  const paidAmount = paid.reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
  const outstandingAmount = (linkedInvoices.length - paid.length > 0)
    ? linkedInvoices.filter(i => i.status !== 'paid').reduce((s, i) => s + (parseFloat(i.total) || 0), 0)
    : 0;

  // Split: invoices auto-generated by the lease billing system (have leaseMonthKey)
  // vs invoices that were manually linked to this contract (no leaseMonthKey)
  const isLeaseContract = lease.type === 'lease';
  const leaseBilled  = linkedInvoices.filter(i => i.leaseMonthKey);
  const manualLinked = linkedInvoices.filter(i => !i.leaseMonthKey);

  // Filtered
  const shown = filterStatus === 'all'
    ? linkedInvoices
    : linkedInvoices.filter(i => i.status === filterStatus);

  function openInvoice(invId) {
    closeModal('lease-invoices');
    setTimeout(() => openModal('view-invoice', invId), 50);
  }

  function unlinkInvoice(inv) {
    if (!updateInvoice) return;
    if (!window.confirm(
      `Unlink ${inv.num || inv.id} from this contract?\n\n` +
      `The pro forma (€${parseFloat(inv.total || 0).toFixed(2)}) will be kept but no longer appear on this lease's pro forma list. ` +
      `You can re-link it to the correct contract via the pro forma itself.`
    )) return;
    updateInvoice(inv.id, { contract: null, contractId: null });
    showToast && showToast('Pro forma unlinked from this contract');
  }

  const StatChip = ({ label, value, color }) => (
    <div style={{
      flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 8,
      background: 'var(--sur2)', border: '1px solid var(--bd)',
    }}>
      <div style={{ fontSize: 10, color: 'var(--tx3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || 'var(--tx)', marginTop: 2 }}>{value}</div>
    </div>
  );

  const StatusPill = ({ status }) => {
    const map = {
      paid:    { bg: 'var(--gr-l)', color: 'var(--gr)', border: 'var(--gr-b)', label: 'Paid' },
      pending: { bg: 'var(--am-l)', color: 'var(--am)', border: 'var(--am-b)', label: 'Pending' },
      issued:  { bg: 'var(--am-l)', color: 'var(--am)', border: 'var(--am-b)', label: 'Issued' },
      overdue: { bg: 'var(--ac-l)', color: 'var(--ac)', border: 'var(--ac-b)', label: 'Overdue' },
      draft:   { bg: 'var(--sur2)', color: 'var(--tx3)', border: 'var(--bd)', label: 'Draft' },
    };
    const s = map[status] || map.draft;
    return (
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '3px 9px', borderRadius: 10,
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap',
      }}>{s.label}</span>
    );
  };

  return (
    <Modal open={!!open} size="lg" onClose={() => closeModal('lease-invoices')}>
      <ModalHeader
        title={`📋 ${lease.type === 'lease' ? 'Lease Pro Formas' : 'Contract Pro Formas'} · ${lease.id}`}
        subtitle={
          lease.type === 'lease'
            ? `${lease.customer} · ${lease.termMonths || 0} month lease · €${lease.monthlyRate || 0}/month`
            : `${lease.customer}${lease.startLabel ? ' · ' + lease.startLabel : ''}${lease.endLabel ? ' → ' + lease.endLabel : ''}`
        }
        onClose={() => closeModal('lease-invoices')}
      />
      <ModalBody>

        {/* ── Summary stats ───────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <StatChip label="Total pro formas" value={linkedInvoices.length} />
          <StatChip label="Paid"     value={paid.length}    color="var(--gr)" />
          <StatChip label="Pending"  value={pending.length} color="var(--am)" />
          <StatChip label="Overdue"  value={overdue.length} color="var(--ac)" />
          <StatChip label="Drafts"   value={drafts.length}  color="var(--tx3)" />
        </div>

        <div style={{
          display: 'flex', gap: 12, marginBottom: 14, padding: '10px 12px',
          background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 8,
          fontSize: 12,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Paid so far</div>
            <div style={{ fontWeight: 700, color: 'var(--gr)', fontSize: 15 }}>€{paidAmount.toFixed(2)}</div>
          </div>
          <div style={{ flex: 1, borderLeft: '1px solid var(--bl-b)', paddingLeft: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Outstanding</div>
            <div style={{ fontWeight: 700, color: outstandingAmount > 0 ? 'var(--am)' : 'var(--gr)', fontSize: 15 }}>€{outstandingAmount.toFixed(2)}</div>
          </div>
          <div style={{ flex: 1, borderLeft: '1px solid var(--bl-b)', paddingLeft: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--tx3)' }}>Total contract</div>
            <div style={{ fontWeight: 700, color: 'var(--bl)', fontSize: 15 }}>€{((lease.agreedPrice) || 0).toLocaleString()}</div>
          </div>
        </div>

        {/* Lease-billed vs manually-linked banner — only for lease contracts where mismatch exists */}
        {isLeaseContract && manualLinked.length > 0 && (
          <div style={{
            display: 'flex', gap: 10, alignItems: 'flex-start',
            background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 8,
            padding: '10px 14px', marginBottom: 12, fontSize: 11,
          }}>
            <span style={{ fontSize: 18 }}>ℹ️</span>
            <div style={{ flex: 1, color: 'var(--tx2)' }}>
              <div>
                <strong style={{ color: 'var(--am)' }}>{manualLinked.length} of {linkedInvoices.length} pro forma{manualLinked.length === 1 ? ' is' : 's are'} not auto-generated by this lease.</strong>
                {' '}They have the contract ID <code style={{ background: 'var(--sur)', padding: '0 4px', borderRadius: 3 }}>{lease.id}</code> tagged on them — either manually picked there, or left over as orphans from a previous contract that re-used the same ID.
                The lease auto-bills <strong>{leaseBilled.length}</strong> matching the €{lease.monthlyRate || 0}/month cadence.
              </div>
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 10, color: 'var(--ac)', borderColor: 'var(--ac-b)' }}
                  onClick={() => {
                    if (!window.confirm(`Unlink all ${manualLinked.length} non-lease-generated pro forma${manualLinked.length === 1 ? '' : 's'} from ${lease.id}?\n\nThe pro formas are preserved on the Pro Formas page and can be re-linked individually.`)) return;
                    manualLinked.forEach(inv => updateInvoice && updateInvoice(inv.id, { contract: null, contractId: null }));
                    showToast && showToast(`${manualLinked.length} pro forma${manualLinked.length === 1 ? '' : 's'} unlinked`);
                  }}
                >
                  ✕ Unlink all {manualLinked.length}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Status filter ──────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
          {[
            { val: 'all',     label: `All (${linkedInvoices.length})` },
            { val: 'paid',    label: `Paid (${paid.length})` },
            { val: 'pending', label: `Pending (${pending.length})` },
            { val: 'overdue', label: `Overdue (${overdue.length})` },
            { val: 'draft',   label: `Drafts (${drafts.length})` },
          ].map(opt => (
            <button
              key={opt.val}
              type="button"
              onClick={() => setFilterStatus(opt.val)}
              className={filterStatus === opt.val ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
              style={{ fontSize: 11 }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* ── Invoices table ──────────────────────────────────────── */}
        {shown.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '40px 20px', color: 'var(--tx3)',
            background: 'var(--sur2)', border: '1px dashed var(--bd)', borderRadius: 8,
          }}>
            <div style={{ fontSize: 32 }}>📄</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)', marginTop: 8 }}>
              {linkedInvoices.length === 0 ? 'No pro formas yet' : 'No pro formas match this filter'}
            </div>
            <div style={{ fontSize: 11, marginTop: 4 }}>
              {linkedInvoices.length === 0
                ? 'Pro formas for this lease will appear here as they are generated each month.'
                : 'Try a different filter or "All".'}
            </div>
          </div>
        ) : (
          <div style={{ border: '1px solid var(--bd)', borderRadius: 8, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{
              display: 'grid', gridTemplateColumns: '110px 1fr 120px 120px 100px 100px 30px',
              gap: 8, padding: '10px 14px', background: 'var(--sur2)',
              fontSize: 10, fontWeight: 700, color: 'var(--tx3)',
              textTransform: 'uppercase', letterSpacing: '.05em',
              borderBottom: '1px solid var(--bd)',
            }}>
              <div>Pro Forma #</div>
              <div>Period / Description</div>
              <div>Issued</div>
              <div>Due</div>
              <div style={{ textAlign: 'right' }}>Amount</div>
              <div>Status</div>
              <div></div>
            </div>
            {/* Rows */}
            {shown.map(inv => {
              const desc = (inv.lines && inv.lines[0]?.desc) || inv.notes || '—';
              const isLeaseBilled = !!inv.leaseMonthKey;
              return (
                <div
                  key={inv.id}
                  onClick={() => openInvoice(inv.id)}
                  style={{
                    display: 'grid', gridTemplateColumns: '110px 1fr 120px 120px 100px 100px 30px',
                    gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--bd)',
                    fontSize: 12, cursor: 'pointer', alignItems: 'center',
                    transition: 'background .12s',
                    background: (isLeaseContract && !isLeaseBilled) ? 'rgba(245, 158, 11, 0.04)' : 'transparent',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--ac-l)'}
                  onMouseLeave={e => e.currentTarget.style.background = (isLeaseContract && !isLeaseBilled) ? 'rgba(245, 158, 11, 0.04)' : 'transparent'}
                >
                  <div style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace" }}>{inv.num || '(draft)'}</div>
                  <div style={{ minWidth: 0, color: 'var(--tx2)' }}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{desc}</div>
                    {isLeaseContract && (
                      <div style={{ marginTop: 3 }}>
                        {isLeaseBilled ? (
                          <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'var(--gr-l)', color: 'var(--gr)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                            🔁 Lease month {inv.leaseMonthKey}
                          </span>
                        ) : (
                          <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: 'var(--am-l)', color: 'var(--am)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                            🔗 Manually linked
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ color: 'var(--tx2)' }}>{inv.issued || '—'}</div>
                  <div style={{ color: inv.status === 'overdue' ? 'var(--ac)' : 'var(--tx2)', fontWeight: inv.status === 'overdue' ? 600 : 400 }}>
                    {inv.due || '—'}
                  </div>
                  <div style={{ textAlign: 'right', fontWeight: 600, fontFamily: "'DM Mono',monospace" }}>
                    €{parseFloat(inv.total || 0).toFixed(2)}
                  </div>
                  <div><StatusPill status={inv.status} /></div>
                  <div style={{ textAlign: 'right', display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isLeaseContract && !isLeaseBilled && (
                      <button
                        type="button"
                        title="Unlink this pro forma from this contract"
                        onClick={e => { e.stopPropagation(); unlinkInvoice(inv); }}
                        style={{ padding: '2px 7px', fontSize: 10, background: 'transparent', border: '1px solid var(--bd)', borderRadius: 4, cursor: 'pointer', color: 'var(--ac)' }}
                      >
                        ✕ Unlink
                      </button>
                    )}
                    <span style={{ color: 'var(--tx3)', fontSize: 14 }}>›</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost" onClick={() => closeModal('lease-invoices')}>Close</button>
      </ModalFooter>
    </Modal>
  );
}
